import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IKeyArray } from 'types/IKeyArray';
import { VariableInstanceType } from 'types/domain/variable';
import {
  actionRequest,
  attemptRequest,
  dialogueRequest,
  suspenseRequest,
  verificationRequest,
} from 'core/services/product';
import {
  IAttempt,
  IAttemptAction,
  IDialogueAction,
  IPlayerResponse,
  ISuspenseAction,
  IVerificationAction,
} from 'types/action/player';
import { ISessionAttempt } from 'types/domain/session';
import { findLastIndex } from 'lodash';
import { SavingState } from 'types/domain/base';
import { hydrate, switchUser } from 'core/actions/product/state';
import { EntityTypes } from 'constants/entities';
import { IText } from 'types/domain/text';
import { ModuleType } from 'types/domain/module';
import { Mode, restartToken } from './session';
import { updateViewerState, ViewerState } from './player';
import { reduceArrayById } from 'utils/reduceArrayById';
import { CourseNodeType } from 'types/domain/course';
import { resetQueue } from './queue';

export interface IRegisterReducer {
  attempts: IKeyArray<IAttemptRegister>;
  history: IHistoryItem[];
  verifications: IKeyArray<IVerificationRegister>;
  dialogues: IKeyArray<IDialogueRegister>;
  index: number;
  isDisabled: boolean;
  isSetIndex: boolean;
  isReset: boolean;
  isNext: boolean;
  isPrev: boolean;
  nextClicked: boolean;
  isViewingChildAction: boolean;
  rendered: {
    [EntityTypes.VERIFICATION_REQUEST]: IKeyArray<boolean>;
    [EntityTypes.ACTION]: IKeyArray<boolean>;
    [EntityTypes.SCENE]: IKeyArray<boolean>;
  };
}

export interface IRendered {
  type: EntityTypes;
  id: number | string;
  rendered?: boolean;
}

export interface IHistoryItem {
  courseProgressId?: number;
  moduleId?: number;
  verificationRequestId?: number;
  sessionAttemptId?: number;
  searchId?: number;
  courseNodeToken?: string;
  courseNodeId?: number;
  modifiedDate?: string;
}

export interface IVerificationRegister {
  verificationRequestId: number;
  courseProgressId: number;
  entities: IKeyArray<IVerificationEntityRegister>;
  created: number;
  saving?: SavingState;
  error?: string;
  text?: IText;
}

export interface IDialogueRegister {
  courseNodeId: number;
  token?: string;
  courseProgressId: number;
  created: number;
  saving?: SavingState;
  error?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  systemId?: string;
  accountMode?: ACCOUNT_MODE;
  code?: string;
  state: DialogueRegisterState;
  courseNodeEntityId?: number;
  text?: IText;
  organisationUserVariableId?: number;
  targetCourseNodeId?: number;
  type?: CourseNodeType;
}

export enum DialogueRegisterState {
  NEW = 0,
  SUBMITTED = 1,
  SUCCESS = 2,
  ERROR = 3,
}

export enum ACCOUNT_MODE {
  LOGIN = 0,
  SIGNUP = 1,
}

export interface IVerificationEntityRegister {
  verificationRequestId: number;
  verificationEntityId: number;
  selected: boolean;
}

export interface IAttemptRegister {
  moduleId?: number;
  type?: ModuleType;
  sessionAttemptId: number;
  courseProgressId?: number;
  searchId?: number;
  actions?: IKeyArray<IActionRegister>;
  variables?: IKeyArray<IVariableRegister>;
  error?: string;
  completed: number;
  created: number;
  attempts: number;
  suspendData?: string;
}

export interface IRegisterAction {
  sessionAttemptId: number;
  courseProgressId: number;
  moduleId: number;
  actionId: number;
  actionEntityId: number;
  selected?: boolean;
  isNew?: boolean;
  isContinue?: boolean;
  result: RegisterResult;
  token?: string;
  tokenCreated?: number;
  state?: RegisterState;
  time?: number;
}

export interface ICompleteAction {
  sessionAttemptId: number;
  courseProgressId: number;
}

export interface IResetAction {
  sessionAttemptId: number;
  courseProgressId: number;
  actionId: number;
  index: number;
  resetEntities?: boolean;
}

export interface ISubmitAction {
  actionId: number;
}

export interface IRegisterActionResponse extends IPlayerResponse {}

export interface IRegisterVariable {
  sessionAttemptId: number;
  variableId: number;
  value: number;
  type: VariableInstanceType;
}

export enum RegisterResult {
  DEFAULT = -1,
  PASS = 1,
  FAIL = 2,
  TIME = 3,
  DECISION = 4,
  INFO = 5,
}
export enum RegisterState {
  NEW = 0,
  STARTED = 1,
  ENDED = 2,
  RESTART = 3,
}

export interface IActionRegister {
  actionId: number;
  moduleId: number;
  actionRegisterId: string;
  result: RegisterResult;
  created: number;
  started: number;
  completed: number;
  entities: IActionEntityRegister;
  sessionAttemptId: number;
  courseProgressId: number;
  saving?: SavingState;
  state: RegisterState;
  time?: number;
  attempts?: number;
}

export interface IVariableRegister {
  id: number;
  value: number;
}

export interface IActionEntityRegister {
  [key: number]: IEntityRegister;
}

export interface IEntityRegister {
  id: number;
  selected: boolean;
  created: number;
}

const initialState: IRegisterReducer = {
  attempts: {},
  verifications: {},
  dialogues: {},
  history: [],
  index: 0,
  isDisabled: false,
  isReset: false,
  isNext: false,
  isPrev: false,
  nextClicked: false,
  isSetIndex: false,
  isViewingChildAction: false,
  rendered: { [EntityTypes.VERIFICATION_REQUEST]: {}, [EntityTypes.ACTION]: {}, [EntityTypes.SCENE]: {} },
};

export enum ActionComplete {
  FORWARD = 0,
  BACKWARD = 1,
}

export type AttemptComplete = {
  sessionAttemptId: number;
};

export interface IPreview {
  moduleId: number;
  actionId: number;
  courseNodeId: number;
  mode?: Mode;
}

export interface INext {
  auto?: boolean;
  interaction?: boolean;
  culprit?: string;
  isChild?: boolean;
}

const registerSlice = createSlice({
  name: 'register',
  initialState,
  reducers: {
    registerAttempt(state, action: PayloadAction<Partial<IAttempt>>) {
      if (!state.attempts[action.payload.sessionAttemptId])
        state.attempts[action.payload.sessionAttemptId] = {
          created: new Date().getTime(),
          actions: {},
          variables: {},
          completed: null,
          sessionAttemptId: action.payload.sessionAttemptId,
          moduleId: action.payload.moduleId,
          type: action.payload.type,
          courseProgressId: action.payload.courseProgressId,
          attempts: 0,
        };

      if (!state.history.some(item => item.sessionAttemptId === action.payload.sessionAttemptId))
        state.history.push({
          sessionAttemptId: action.payload.sessionAttemptId,
          courseProgressId: action.payload.courseProgressId,
        });
    },
    registerAction(state, action: PayloadAction<Partial<IRegisterAction>>) {
      state.isReset = false;

      const attempt = state.attempts[action.payload.sessionAttemptId];

      if (!attempt) return console.warn('Cannot register action');

      attempt.actions[action.payload.actionId] = {
        actionId: action.payload.actionId,
        moduleId: action.payload.moduleId,
        actionRegisterId: `${action.payload.actionId}`,
        courseProgressId: action.payload.courseProgressId,
        sessionAttemptId: action.payload.sessionAttemptId,
        entities: {},
        created: new Date().getTime(),
        started: new Date().getTime(),
        completed: null,
        result: null,
        state: action.payload.state || RegisterState.NEW,
        attempts: 0,
      };
    },
    resetAction(state, action: PayloadAction<IResetAction>) {
      state.index = action.payload.index;
      const attempt = state.attempts[action.payload.sessionAttemptId];
      if (!attempt) return;
      if (attempt.actions[action.payload.actionId]) {
        const actionHistory = Object.values(attempt.actions).sort((a, b) => (a.created < b.created ? -1 : 1));
        const index = actionHistory.findIndex(a => a.actionId === action.payload.actionId);
        attempt.actions = reduceArrayById(actionHistory.slice(0, index + 1), 'actionId');
        attempt.actions[action.payload.actionId] = {
          ...attempt.actions[action.payload.actionId],
          created: new Date().getTime(),
          started: new Date().getTime(),
          completed: null,
          result: null,
          entities: action.payload.resetEntities ? {} : attempt.actions[action.payload.actionId].entities,
          state: RegisterState.NEW,
        };
      }
      // const queueIndex = state.queue[action.payload.courseProgressId]
      //   ? state.queue[action.payload.courseProgressId].findIndex(
      //       a => a.sessionAttemptId === action.payload.sessionAttemptId,
      //     )
      //   : null;

      // if (queueIndex)
      //   state.queue[action.payload.courseProgressId] = state.queue[action.payload.courseProgressId].slice(
      //     0,
      //     queueIndex + 1,
      //   );

      // const historyIndex = state.history.findIndex(a => a.sessionAttemptId === action.payload.sessionAttemptId);
      // state.history = state.history.slice(0, historyIndex + 1);
    },
    registerActionState(state, action: PayloadAction<Partial<IRegisterAction>>) {
      const attempt = state.attempts[action.payload.sessionAttemptId];
      if (!attempt) return console.warn('Cannot register action state');
      if (!attempt.actions[action.payload.actionId]) return console.warn('Action is not registered');
      if (action.payload.state) attempt.actions[action.payload.actionId].state = action.payload.state;
      if (action.payload.time && action.payload.time !== null)
        attempt.actions[action.payload.actionId].time = action.payload.time;
      switch (action.payload.state) {
        case RegisterState.STARTED:
          attempt.actions[action.payload.actionId].started = new Date().getTime();

          break;
        case RegisterState.ENDED:
          attempt.actions[action.payload.actionId].completed = new Date().getTime();
          break;
        case RegisterState.RESTART:
          attempt.actions[action.payload.actionId].started = new Date().getTime();
          attempt.actions[action.payload.actionId].completed = null;
          attempt.actions[action.payload.actionId].entities = {};
          break;
      }
    },
    registerActionResult(state, action: PayloadAction<Partial<IRegisterAction>>) {
      const attempt = state.attempts[action.payload.sessionAttemptId];
      if (!attempt) return console.warn('Cannot register action result');

      if (!attempt.actions[action.payload.actionId]) return console.warn('Action is not registered');

      if (action.payload.result !== undefined) {
        attempt.actions[action.payload.actionId].result = action.payload.result;
        attempt.actions[action.payload.actionId].completed = new Date().getTime();
      }
    },
    registerActionEntity(state, action: PayloadAction<Partial<IRegisterAction>>) {
      const attempt = state.attempts[action.payload.sessionAttemptId];

      if (!attempt) return console.warn('Cannot register action');
      if (action.payload.actionEntityId && attempt.actions[action.payload.actionId])
        attempt.actions[action.payload.actionId].entities[action.payload.actionEntityId] = {
          id: action.payload.actionEntityId,
          selected: action.payload.selected
            ? action.payload.selected
            : attempt.actions[action.payload.actionId].entities[action.payload.actionEntityId]
            ? attempt.actions[action.payload.actionId].entities[action.payload.actionEntityId].selected
              ? false
              : true
            : true,
          created: new Date().getTime(),
        };
    },
    registerModule(state, action) {
      const attempt = state.attempts[action.payload.sessionAttemptId];

      if (!attempt) return;

      attempt.moduleId = action.payload.moduleId;
    },
    actionsComplete(state) {
      state.isNext = true;
    },
    actionSubmit(state, action: PayloadAction<ISubmitAction>) {
      state.isNext = true;
    },
    attemptComplete(state, action: PayloadAction<AttemptComplete>) {
      if (action.payload && action.payload.sessionAttemptId)
        state.attempts[action.payload.sessionAttemptId] = {
          ...state.attempts[action.payload.sessionAttemptId],
          completed: new Date().getTime(),
        };
    },
    preview(state, action: PayloadAction<IPreview>) {
      state.index = 0;
      state.history = [];
      state.attempts = {};
      state.dialogues = {};
      // A next still pending from the run being replaced (e.g. the preview's video had
      // just ended when Edit was pressed) would otherwise be taken by startAction, moving
      // the editor off the slide it was opened on.
      state.isNext = false;
    },
    clearRegister(state) {
      state.index = 0;
      state.history = [];
      state.attempts = {};
      state.dialogues = {};
    },
    resetIndex(state) {
      state.index = 0;
      state.isReset = true;
    },
    setIndex(state, action: PayloadAction<number>) {
      state.index = action.payload;
      state.isSetIndex = true;
      // state.history = state.history.slice(0, action.payload);
    },
    goToIndex(state, action: PayloadAction<number>) {
      state.index = action.payload;
    },
    next(state, action: PayloadAction<INext>) {
      state.isNext = action.payload ? action.payload.auto : false;
      state.nextClicked = true;
      state.isDisabled = true;
      state.isViewingChildAction = action.payload.isChild;
    },
    disabled(state, action: PayloadAction<boolean>) {
      state.isDisabled = action.payload;
    },
    prev(state, action: PayloadAction<boolean>) {
      state.isPrev = action.payload;
    },
    proceed(state) {
      state.isDisabled = false;
    },
    exit(state) {
      // state.index = 0;
      // state.history = [];
    },
    // Optional payload: the index the caller observed when it decided to advance.
    // One click of next (or one video-ended event) must move exactly one action,
    // but several independent paths can each reach an increment — a duplicate
    // next(), two nextActionSagas racing to proceed(), or a late proceedSaga
    // finishing its asset/video wait after the learner already moved. Anchoring
    // the advance to the index it was decided from makes those extras no-ops
    // instead of a second increment, which is what skips a slide. Callers that
    // pass nothing keep the old unconditional behaviour.
    incrementIndex(state, action: PayloadAction<number | undefined>) {
      if (action?.payload != null && action.payload !== state.index) return;
      state.index++;
      state.isNext = false;
      state.isPrev = false;
    },
    decrementIndex(state) {
      if (state.index === 0) return;
      state.index--;
      state.isPrev = false;
    },
    updateRendered(state, action: PayloadAction<IRendered>) {
      state.rendered = {
        ...state.rendered,
        [action.payload.type]: { ...state.rendered[action.payload.type], [action.payload.id]: action.payload.rendered },
      };
    },

    updateVerification(state, action: PayloadAction<IHistoryItem>) {
      state.verifications[action.payload.verificationRequestId] = {
        ...state.verifications[action.payload.verificationRequestId],
        ...action.payload,
      };
    },
    registerDialogue(state, action: PayloadAction<IDialogueRegister>) {
      state.dialogues[action.payload.courseNodeId] = action.payload;
      if (!state.history.some(hi => hi.courseNodeId === action.payload.courseNodeId)) {
        const item = {
          token: action.payload.token,
          courseProgressId: action.payload.courseProgressId,
          created: new Date().getTime(),
          courseNodeId: action.payload.courseNodeId,
          type: action.payload.type,
        };
        state.history.push(item);
      }
    },
    updateDialogueState(state, action: PayloadAction<{ id: number; state: DialogueRegisterState }>) {
      state.dialogues[action.payload.id].state = action.payload.state;
    },
    resetDialogue(state, action: PayloadAction<{ id: number }>) {
      state.dialogues[action.payload.id].state = DialogueRegisterState.NEW;
      state.dialogues[action.payload.id].error = null;
    },
  },
  extraReducers: builder => {
    // builder.addCase(updateToken, (state, action) => {
    //   state.queue = {};
    // });
    builder.addCase(updateViewerState, (state, action) => {
      if (action.payload.state === ViewerState.RESTING && state.isDisabled === true) state.isDisabled = false;
      if (action.payload.state === ViewerState.RESTING && state.isSetIndex === true) state.isSetIndex = false;
    });
    builder.addCase(attemptRequest.pending, (state, action) => {
      const { offlineId, courseProgressId, type, moduleId, searchId } = action.meta.arg as IAttemptAction;
      state.attempts[offlineId] = {
        actions: {},
        variables: {},
        completed: null,
        courseProgressId,
        sessionAttemptId: offlineId,
        searchId,
        type,
        moduleId,
        created: new Date().getTime(),
        attempts: state.attempts[offlineId] ? state.attempts[offlineId].attempts + 1 : 0,
      };

      const item = {
        courseProgressId,
        created: new Date().getTime(),
        sessionAttemptId: offlineId,
        searchId,
        type: CourseNodeType.ASSIGN,
      };

      state.history.push(item);
    });

    builder.addCase(attemptRequest.rejected, (state, action) => {
      const { offlineId } = action.meta.arg as IAttemptAction;
      if (state.attempts[offlineId]) state.attempts[offlineId].error = action.error.message;
    });
    builder.addCase(attemptRequest.fulfilled, (state, action) => {
      const { offlineId } = action.meta.arg as IAttemptAction;
      const sessionAttempt = action.payload as ISessionAttempt;
      state.attempts[sessionAttempt.id] = {
        ...sessionAttempt,
        ...state.attempts[offlineId],
        sessionAttemptId: sessionAttempt.id,
        actions: Object.values(state.attempts[offlineId].actions).reduce((prev, next) => {
          return { ...prev, [next.actionId]: { ...next, sessionAttemptId: sessionAttempt.id } };
        }, {}),
      };
      const index = findLastIndex(state.history, h => h.sessionAttemptId === offlineId);
      state.history[index] = { ...state.history[index], sessionAttemptId: sessionAttempt.id };
      delete state.attempts[offlineId];
    });
    builder.addCase(actionRequest.pending, (state, action) => {
      const { sessionAttemptId, actionId } = action.meta.arg as IRegisterAction;
      state.attempts[sessionAttemptId].actions[actionId].saving = SavingState.SAVING;
      state.attempts[sessionAttemptId].actions[actionId].attempts =
        state.attempts[sessionAttemptId].actions[actionId].attempts + 1;
    });
    builder.addCase(actionRequest.rejected, (state, action) => {
      const { sessionAttemptId, actionId } = action.meta.arg as IRegisterAction;
      if (state.attempts[sessionAttemptId] && state.attempts[sessionAttemptId].actions[actionId])
        state.attempts[sessionAttemptId].actions[actionId].saving = SavingState.ERROR;
    });
    builder.addCase(actionRequest.fulfilled, (state, action) => {
      const { sessionAttemptId, actionId } = action.meta.arg as IRegisterAction;
      if (state.attempts[sessionAttemptId].actions[actionId])
        state.attempts[sessionAttemptId].actions[actionId].saving = SavingState.SAVED;
    });
    builder.addCase(dialogueRequest.pending, (state, action) => {
      const { courseNodeId, courseProgressId, token } = action.meta.arg as IDialogueAction;
      state.dialogues[courseNodeId] = {
        ...action.meta.arg,
        created: new Date().getTime(),
        code: null,
        saving: SavingState.SAVING,
        state: action.meta.arg.state || DialogueRegisterState.NEW,
        type: action.meta.arg.type || CourseNodeType.ASSIGN,
      };
    });
    builder.addCase(dialogueRequest.rejected, (state, action) => {
      const { courseNodeId } = action.meta.arg as IDialogueAction;

      if (state.dialogues[courseNodeId]) {
        state.dialogues[courseNodeId].state = DialogueRegisterState.ERROR;
        state.dialogues[courseNodeId].saving = SavingState.SAVED;
        state.dialogues[courseNodeId].error = action.error.message;
      }
    });
    builder.addCase(dialogueRequest.fulfilled, (state, action) => {
      const { courseNodeId, autoNext } = action.meta.arg as IDialogueAction;
      if (state.dialogues[courseNodeId]) {
        if (action.meta.arg.autoNext) state.dialogues[courseNodeId].state = DialogueRegisterState.SUCCESS;
        state.dialogues[courseNodeId].saving = SavingState.SAVED;
        state.dialogues[courseNodeId].code = action.payload.code;
      }
      if (autoNext) state.isNext = true;
    });

    builder.addCase(restartToken, (state, action) => {
      state.history = [];
      //state.queue[`${action.payload.token}.${action.payload.courseProgressId}`] = [];
      state.index = 0;
      state.dialogues = {};
      state.attempts = reduceArrayById(
        Object.values(state.attempts).filter(
          (attempt: IAttemptRegister) => attempt.courseProgressId !== action.payload.courseProgressId,
        ),
        'sessionAttemptId',
      );
    });
    builder.addCase(resetQueue, (state, action) => {
      const index = state.history.findIndex(historyItem => historyItem.courseNodeId === action.payload.courseNodeId);
    });
    builder.addCase(suspenseRequest.pending, (state, action) => {
      const { sessionAttemptId, suspendData } = action.meta.arg as ISuspenseAction;
      if (!state.attempts[sessionAttemptId])
        state.attempts[sessionAttemptId] = {
          sessionAttemptId,
          created: new Date().getTime(),
          completed: null,
          attempts: 0,
        };
      state.attempts[sessionAttemptId].suspendData = suspendData;
    });
    // builder.addCase(updateToken, state => {
    //   state.index = 0;
    //   state.history = [];
    // });
    // Attempts, answers, dialogue submissions and history all belong to whoever
    // was signed in when they were recorded. Start the new user from scratch.
    builder.addCase(switchUser, () => ({
      ...initialState,
      attempts: {},
      verifications: {},
      dialogues: {},
      history: [],
      rendered: { [EntityTypes.VERIFICATION_REQUEST]: {}, [EntityTypes.ACTION]: {}, [EntityTypes.SCENE]: {} },
    }));
    builder.addCase(hydrate, (state, action) => {
      if (action.payload.register)
        Object.keys(action.payload.register).forEach(type => {
          if (state[type] && !['isNext', 'history', 'index'].includes(type))
            state[type] = { ...state[type], ...action.payload.register[type] };

          if (['history'].includes(type)) state[type] = [...state[type], ...action.payload.register[type]];

          // if (['index'].includes(type)) state[type] = action.payload.register[type];
        });

      // Nothing is in flight on boot. A save that was still pending when the register was
      // last stored (the tab closed, or the request failed - a rejection isn't persisted)
      // would otherwise read as saving forever, and hold the spinner on that slide.
      const settle = (item: { saving?: SavingState }) => {
        if (item?.saving === SavingState.SAVING) item.saving = SavingState.ERROR;
      };
      Object.values(state.attempts).forEach(attempt => Object.values(attempt?.actions ?? {}).forEach(settle));
      Object.values(state.dialogues).forEach(settle);
      Object.values(state.verifications).forEach(settle);
    });
  },
});

export const {
  registerAttempt,
  registerAction,
  resetAction,
  registerActionResult,
  registerActionState,
  registerActionEntity,
  registerModule,
  actionsComplete,
  attemptComplete,
  updateVerification,
  updateDialogueState,
  resetDialogue,
  registerDialogue,
  updateRendered,
  decrementIndex,
  incrementIndex,
  resetIndex,
  clearRegister,
  goToIndex,
  next,
  disabled,
  prev,
  preview,
  proceed,
  actionSubmit,
  setIndex,
  exit,
} = registerSlice.actions;
export default registerSlice.reducer;
